import { standardizeStartOfDateUTC, standardizeEndOfDateUTC } from './dates'
import moment from 'moment-timezone'
import { dangers, warnings } from './colors'
export const TRAINING_LOCATION_TEMPLATE = {
	name: '',
	abbreviation: '',
	market_id: '',
	facility_id: '',
	external_notes: '',
	internal_notes: '',
	address: {
		address_line_1: '',
		address_line_2: '',
		city: '',
		state: '',
		zip_code: ''
	}
}

export const TRAINING_COURSE_TEMPLATE = {
	name: '',
	short_name: '',
	training_course_identifier_id: '',
	facility_id: '',
	course_id: '',
	sessions: [],
	description: '',
	short_description: '',
	target_audience_information: '',
	price_per_session: null,
	number_of_sessions: null,
	session_length: null,
	capacity_min: null,
	capacity_max: null,
	prerequisite_skills: [],
	age_min_months: null,
	age_max_months: null,
	size_restrictions: [],
	required_vaccines: [],
	deactivated: false,
	// The following are not part of the model schema. only used for ui
	age_category: '',
	course_id_prefix: '',
	course_id_suffix: '',
	number_of_classes: 0
}

export const TRAINING_SESSION_TEMPLATE = {
	session: 0,
	overview: '',
	training_materials: []
}

export const TRAINING_MATERIAL_TEMPLATE = {
	// type: '', // not used at this time
	use: 'homework',
	name: '',
	description: '',
	file_path: '',
	file_type: '',
	// The following are not part of the db model schema. only used for ui
	path_error: ''
}

export const TRAINING_CLASS_TEMPLATE = {
	training_course_id: '',
	class_id: '',
	dates: [],
	start_time: '',
	end_time: '',
	access: '',
	instructor_id: '',
	training_location_id: '',
	capacity_max: null,
	// The following are not part of the db model schema. only used for ui
	capacity_max_override: false,
	instructor_name: '',
	location_name: '',
	has_transfers: false,
	has_existing_adjustments: false // this is passed back from the api for existing classes, but is not part of the db model schema
}

export const TRAINING_SESSION_LENGTHS = {
	30: '30 minutes',
	45: '45 minutes',
	60: '60 minutes',
	90: '90 minutes'
}

export const TRAINING_AGE_CATEGORIES = {
	Puppy: 'puppy',
	AdvancedPuppy: 'advanced_puppy',
	AllAges: 'all_ages'
}

export const TRAINING_COURSE_ABBREVIATIONS = {
	PVT: 'PVT',
	FVRTS: 'FVRTS'
}
export const TRAINING_AGE_MONTH_REQUIREMENTS = {
	[TRAINING_AGE_CATEGORIES.Puppy]: [0, 4],
	[TRAINING_AGE_CATEGORIES.AdvancedPuppy]: [4, 12],
	[TRAINING_AGE_CATEGORIES.AllAges]: [null, null]
}

export const TRAINING_AGE_LABELS = {
	[TRAINING_AGE_CATEGORIES.Puppy]: 'Puppy (<4 months)',
	[TRAINING_AGE_CATEGORIES.AdvancedPuppy]: 'Advanced Puppy (4-12 months)',
	[TRAINING_AGE_CATEGORIES.AllAges]: 'All'
}

export const TRAINING_COURSE_CATEGORY = {
	Group: 'group',
	Private: 'private',
	FacilityVisitSession: 'facility_visit_session'
}

export const NUMBER_OF_SESSIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export const TRAINING_PAGE_IDS = {
	ScheduleView: 'schedule-view',
	ClassView: 'class-view'
}

export const getClassStatus = (classDetails, facility) => {
	if (!classDetails) {
		return null
	}

	const { dates, start_time, end_time } = classDetails
	let [startHours, startMinutes] = start_time.split(':')
	let [endHours, endMinutes] = end_time.split(':')

	const timeZone = facility?.time_zone || 'America/New_York'
	const nowInTz = moment().tz(timeZone)

	const start = standardizeStartOfDateUTC(dates[0])
	const end = standardizeEndOfDateUTC(dates[dates.length - 1])

	const startDateTime = nowInTz.clone().set({
		month: start.month(),
		date: start.date(),
		year: start.year(),
		hours: startHours,
		minutes: startMinutes,
		seconds: 0,
		milliseconds: 0
	})

	const endDateTime = nowInTz.clone().set({
		month: end.month(),
		date: end.date(),
		year: end.year(),
		hours: endHours,
		minutes: endMinutes,
		minutes: startMinutes,
		seconds: 0,
		milliseconds: 0
	})

	const isBetween = nowInTz.isBetween(startDateTime, endDateTime, null, '[]')
	const isBefore = nowInTz.isBefore(startDateTime)
	const isAfter = nowInTz.isAfter(endDateTime)

	return { isBetween, isBefore, isAfter }
}

export const petMeetsCourseCriteria = ({ pet, startDate, endDate, course }) => {
	if (!pet || !course) {
		return false
	}

	const payload = {
		meets_requirements: true,
		issues: []
	}

	const { age_min_months, age_max_months, size_restrictions, required_vaccines, prerequisite_skills } = course
	const {
		birthday,
		size,
		vaccines,
		training_skills = [],
		temporary_service_restrictions = [],
		permanent_service_restrictions = []
	} = pet

	if (
		temporary_service_restrictions.includes('Training') ||
		permanent_service_restrictions.includes('Training')
	) {
		payload.meets_requirements = false
		payload.issues.push({ message: 'Pet has training restrictions', color: warnings[400], type: 'warning' })
		return payload
	}

	const bd = moment(birthday)
	const sd = moment(startDate)
	const ed = moment(endDate)
	let diff = sd.diff(bd, 'months')

	if (diff < 0) {
		diff = 0
	}

	if (age_min_months && diff <= age_min_months) {
		payload.meets_requirements = false
		payload.issues.push({ message: 'Pet is too young for class', color: warnings[400], type: 'warning' })
	}

	if (age_max_months && diff >= age_max_months) {
		payload.meets_requirements = false
		payload.issues.push({ message: 'Pet is too old for class', color: warnings[400], type: 'warning' })
	}

	// if (size_restrictions.length && !size_restrictions.includes(size)) {
	// 	payload.meets_requirements = false
	// 	payload.issues.push({ message: 'Pet does not meet size requirements', color: warnings[400], type: 'warning'})
	// }

	// if (prerequisite_skills.length) {
	// 	const hasAll = prerequisite_skills.every((s) => training_skills.includes(s._id))
	// 	if (!hasAll) {
	// 		payload.meets_requirements = false
	// 		payload.issues.push({ message: 'Pet does not meet prerequisite skills', color: warnings[400], type: 'warning'})
	// 	}
	// }

	if (required_vaccines.length) {
		const allValid = required_vaccines.every((v) =>
			vaccines.some((pv) => pv.id === v && ed.isBefore(moment(pv.expiration_date)))
		)
		if (!allValid) {
			payload.meets_requirements = false
			payload.issues.push({
				message: 'Pet does not meet vaccine requirements',
				color: dangers[400],
				type: 'danger'
			})
		}
	}

	return payload
}

export const TRAINING_ATTENDANCE_STATUSES = {
	PENDING: 1, // INITIAL STATUS
	CHECKED_IN: 4, // ANALAGOUS TO IN_PROGRESS IN APPOINTMENT STATUS
	ABSENT: 7, // ANALAGOUS TO CANCELED IN APPOINTMENT STATUS
	NO_SHOW: 8, // ANALAGOUS TO NO_SHOW IN APPOINTMENT STATUS
	UNENROLLED: 9,
	TRANSFERRED: 10 // NOT IMPLEMENTED IN APPOINTMENT STATUS
}
